/**
 * API Client
 *
 * Handles all API requests with:
 * - Access Token in Memory (NOT localStorage!)
 * - Refresh Token via HttpOnly Cookie (automatic)
 * - Automatic token refresh on 401
 * - Error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG, API_ENDPOINTS } from '../constants/api';
import { TIMEOUTS } from '../constants/timeouts';
import { logger } from './logger';

// Re-export analytics types for convenience
export type { WorkflowRegistry, RegisterWorkflowInput, WorkflowExecutionResult } from '../types/analytics';

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: string;
    tenantId: string;
  };
}

export interface RefreshResponse {
  accessToken: string;
}

export interface Workflow {
  id: string | number; // n8n can use string IDs or numeric IDs
  name: string;
  active: boolean;
  nodes: unknown[];
  connections: unknown;
  createdAt: string;
  updatedAt: string;
  settings?: unknown;
  tags?: Array<{ id: number; name: string }>;
}

export interface Execution {
  id: string;
  finished: boolean;
  mode: string;
  retryOf?: string;
  retrySuccessId?: string;
  startedAt: string;
  stoppedAt?: string;
  workflowId: string;
  workflowData?: {
    id: string;
    name: string;
  };
}

class ApiClient {
  private accessToken: string | null = null;
  private axiosInstance: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      withCredentials: API_CONFIG.WITH_CREDENTIALS,
      timeout: TIMEOUTS.API_REQUEST,
      headers: API_CONFIG.HEADERS,
    });

    // Request interceptor: Add access token to headers
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: Handle 401 and refresh token
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If 401 and not already retried, try to refresh token
        // BUT: Don't retry if it's the refresh endpoint itself (to avoid infinite loop)
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            // Retry original request with new token
            if (this.accessToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${this.accessToken}`;
            }
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear token and dispatch logout event
            this.accessToken = null;

            // Dispatch custom event to trigger logout
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('auth:session-expired'));
            }

            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set access token in memory
   * CRITICAL: Never store in localStorage!
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Login user
   * POST /auth/login
   */
  async login(email: string, password: string, tenantDomain: string): Promise<LoginResponse> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<LoginResponse>>(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
        tenantDomain,
      });

      if (response.data.success && response.data.data) {
        // Store access token in memory (NOT localStorage!)
        this.accessToken = response.data.data.accessToken;
        return response.data.data;
      }

      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as ApiResponse)?.message ||
          error.message ||
          'Login failed';
        throw new Error(message);
      }
      throw error;
    }
  }

  /**
   * Refresh access token
   * POST /auth/refresh
   * Uses refresh token from HttpOnly cookie (automatic)
   */
  async refreshToken(): Promise<void> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await this.axiosInstance.post<ApiResponse<RefreshResponse>>(
          API_ENDPOINTS.AUTH.REFRESH
        );

        if (response.data.success && response.data.data) {
          // Update access token in memory
          this.accessToken = response.data.data.accessToken;
        } else {
          throw new Error(response.data.message || 'Token refresh failed');
        }
      } catch (error) {
        // Clear token on refresh failure
        this.accessToken = null;
        if (axios.isAxiosError(error)) {
          const message =
            (error.response?.data as ApiResponse)?.message ||
            error.message ||
            'Token refresh failed';
          throw new Error(message);
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Logout user
   * POST /auth/logout
   */
  async logout(): Promise<void> {
    try {
      await this.axiosInstance.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      this.accessToken = null;
    }
  }

  /**
   * Get current user info from backend
   * GET /auth/me
   * Requires valid access token
   */
  async getCurrentUser(): Promise<{ id: string; email: string; role: string; tenantId: string }> {
    return this.get<{ id: string; email: string; role: string; tenantId: string }>(API_ENDPOINTS.AUTH.ME);
  }

  /**
   * Generic request method
   */
  async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: unknown
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.request<ApiResponse<T>>({
        method,
        url: endpoint,
        data,
      });

      if (response.data.success) {
        // Some endpoints (like DELETE) don't return data, only success message
        if (response.data.data !== undefined) {
          return response.data.data as T;
        }
        // For endpoints without data (like DELETE), return empty object or message
        return (response.data.message || true) as T;
      }

      throw new Error(response.data.message || 'Request failed');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as ApiResponse)?.message ||
          error.message ||
          'Request failed';
        throw new Error(message);
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>('GET', endpoint);
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>('POST', endpoint, data);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>('PUT', endpoint, data);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>('DELETE', endpoint);
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>('PATCH', endpoint, data);
  }

  // Specific API methods

  /**
   * Get workflows from n8n
   * GET /api/n8n/workflows
   */
  async getWorkflows(): Promise<Workflow[]> {
    return this.get<Workflow[]>(API_ENDPOINTS.N8N.WORKFLOWS);
  }

  /**
   * Execute workflow
   * POST /api/n8n/workflows/:id/execute
   */
  async executeWorkflow(workflowId: string | number, inputData?: unknown): Promise<unknown> {
    // Validate workflowId before making the request
    if (!workflowId) {
      throw new Error(`Invalid workflow ID: ${workflowId}`);
    }

    try {
      logger.debug('Executing workflow via API', { 
        workflowId, 
        workflowIdType: typeof workflowId,
        hasInputData: !!inputData 
      });
      
      const result = await this.post<unknown>(API_ENDPOINTS.N8N.EXECUTE_WORKFLOW(workflowId), { inputData });
      
      logger.info('Workflow execution successful', { workflowId });
      
      return result;
    } catch (error) {
      logger.error('Workflow execution failed', { 
        workflowId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      
      // Re-throw with more context
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`Workflow ${workflowId} not found. The workflow may not exist in n8n or the n8n URL is incorrect.`);
        }
        throw error;
      }
      throw new Error('Failed to execute workflow. Please check your n8n configuration.');
    }
  }

  /**
   * Set workflow active status
   * PATCH /api/n8n/workflows/:id/active
   * @param workflowId The workflow ID in n8n
   * @param active Whether the workflow should be active
   */
  async setWorkflowActive(workflowId: string | number, active: boolean): Promise<Workflow> {
    try {
      logger.debug('Setting workflow active status via API', {
        workflowId,
        active,
      });

      const result = await this.patch<Workflow>(API_ENDPOINTS.N8N.SET_WORKFLOW_ACTIVE(workflowId), { active });

      logger.info('Workflow active status updated', { workflowId, active: result.active });

      return result;
    } catch (error) {
      logger.error('Failed to set workflow active status', {
        workflowId,
        active,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Get executions from n8n
   * GET /api/n8n/executions
   * @param workflowId Optional workflow ID to filter executions
   * @param limit Optional limit (default 100, max 1000)
   */
  async getExecutions(workflowId?: string | number, limit?: number): Promise<Execution[]> {
    const params = new URLSearchParams();
    if (workflowId !== undefined) {
      params.append('workflowId', workflowId.toString());
    }
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    const queryString = params.toString();
    return this.get<Execution[]>(`${API_ENDPOINTS.N8N.EXECUTIONS}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get all workflow tag metrics (Admin-only)
   * GET /admin/workflow-tags/metrics
   */
  async getWorkflowTagMetrics(): Promise<import('../types/analytics').WorkflowTagMetrics[]> {
    return this.get<import('../types/analytics').WorkflowTagMetrics[]>(API_ENDPOINTS.ADMIN.WORKFLOW_TAG_METRICS);
  }

  /**
   * Get specific workflow tag metrics (Admin-only)
   * GET /admin/workflow-tags/:tagName
   */
  async getWorkflowTagMetricsByTag(tagName: string): Promise<import('../types/analytics').WorkflowTagMetrics> {
    return this.get<import('../types/analytics').WorkflowTagMetrics>(API_ENDPOINTS.ADMIN.WORKFLOW_TAG(tagName));
  }

  /**
   * Create or update workflow tag metrics (Admin-only)
   * PUT /admin/workflow-tags/:tagName
   */
  async upsertWorkflowTagMetrics(
    tagName: string,
    data: { estimated_time_saved_minutes: number; hourly_rate_euros?: number; cost_per_execution?: number }
  ): Promise<import('../types/analytics').WorkflowTagMetrics> {
    return this.put<import('../types/analytics').WorkflowTagMetrics>(API_ENDPOINTS.ADMIN.WORKFLOW_TAG(tagName), data);
  }

  /**
   * Delete workflow tag metrics (Admin-only)
   * DELETE /admin/workflow-tags/:tagName
   */
  async deleteWorkflowTagMetrics(tagName: string): Promise<void> {
    return this.delete<void>(API_ENDPOINTS.ADMIN.WORKFLOW_TAG(tagName));
  }

  /**
   * Get analytics for current tenant
   * GET /analytics?timeframe=week|month|year|all
   * @param timeframe Optional timeframe filter (default: 'week')
   */
  async getAnalytics(timeframe?: import('../types/analytics').TimeframeType): Promise<import('../types/analytics').AnalyticsSummary> {
    const params = timeframe ? `?timeframe=${timeframe}` : '';
    return this.get<import('../types/analytics').AnalyticsSummary>(`${API_ENDPOINTS.ANALYTICS}${params}`);
  }

  // ============================================================================
  // Workflow Registry Methods
  // ============================================================================

  /**
   * Get all registered workflows
   * GET /workflow-registry
   */
  async getWorkflowRegistry(): Promise<import('../types/analytics').WorkflowRegistry[]> {
    return this.get<import('../types/analytics').WorkflowRegistry[]>(API_ENDPOINTS.WORKFLOW_REGISTRY.LIST);
  }

  /**
   * Get single workflow by ID
   * GET /workflow-registry/:id
   */
  async getWorkflowRegistryById(id: string): Promise<import('../types/analytics').WorkflowRegistry> {
    return this.get<import('../types/analytics').WorkflowRegistry>(API_ENDPOINTS.WORKFLOW_REGISTRY.GET(id));
  }

  /**
   * Register new workflow (Admin-only)
   * POST /workflow-registry
   */
  async registerWorkflow(
    data: import('../types/analytics').RegisterWorkflowInput
  ): Promise<import('../types/analytics').WorkflowRegistry> {
    return this.post<import('../types/analytics').WorkflowRegistry>(API_ENDPOINTS.WORKFLOW_REGISTRY.LIST, data);
  }

  /**
   * Update workflow (Admin-only)
   * PATCH /workflow-registry/:id
   */
  async updateWorkflow(
    id: string,
    data: Partial<import('../types/analytics').RegisterWorkflowInput> & { is_active?: boolean }
  ): Promise<import('../types/analytics').WorkflowRegistry> {
    return this.patch<import('../types/analytics').WorkflowRegistry>(API_ENDPOINTS.WORKFLOW_REGISTRY.GET(id), data);
  }

  /**
   * Delete workflow (Admin-only)
   * DELETE /workflow-registry/:id
   */
  async deleteWorkflow(id: string): Promise<void> {
    return this.delete<void>(API_ENDPOINTS.WORKFLOW_REGISTRY.GET(id));
  }

  /**
   * Execute registered workflow via webhook
   * POST /workflow-registry/:id/execute
   */
  async executeRegisteredWorkflow(
    id: string,
    payload?: unknown
  ): Promise<import('../types/analytics').WorkflowExecutionResult> {
    try {
      logger.debug('Executing registered workflow via API', {
        registryId: id,
        hasPayload: !!payload,
      });

      const result = await this.post<import('../types/analytics').WorkflowExecutionResult>(
        API_ENDPOINTS.WORKFLOW_REGISTRY.EXECUTE(id),
        { payload }
      );

      logger.info('Registered workflow execution successful', { registryId: id, success: result.success });

      return result;
    } catch (error) {
      logger.error('Registered workflow execution failed', {
        registryId: id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      throw error;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();


